// BatchRow / BatchTable: the Workspace batch list (DESIGN §3). Columns BATCH · STAGE · OWNER · DUE · STATUS.
// The whole row is one link to the batch; no per-row buttons. Below 768px each row is a compact 3-line card.
import './batch-row.css';
import { batchLabel, monthLabel } from '../../shared/constants.js';
import { formatDate } from '../../shared/dates.js';
import { isOverdue } from '../../shared/derive.js';
import { batchHref } from '../router.js';
import { StageProgress } from './stage-progress.jsx';
import { Avatar, EmptyState, StatusBadge, cx } from './ui.jsx';

const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// Plain facts about a batch for display: { client, month, owner, context, due, overdue }.
export function batchFacts(batch, index, today) {
  const client = index?.clientsById?.get(batch.clientId);
  const month = index?.monthsById?.get(batch.monthId);
  const owner = batch.owner ? index?.teamById?.get(batch.owner) : null;
  const context = [client?.name, batchLabel(batch.batchNumber), month && monthLabel(month.monthNumber)].filter(Boolean).join(' · ');
  const overdue = !!today && isOverdue(batch, today, index); // an Off month is never late (DESIGN §7.5)
  return { client, month, owner, context, due: formatDate(batch.dueDate) || '—', overdue };
}

// showClient / showMonth: parts of the secondary line ("Client · Batch 06 · Month 09", DESIGN §7.4); drop what the page
// already shows (client workspace → "Batch 06 · Month 09"; inside one month → "Batch 06"). showOwner: false on a person page.
export function BatchRow({ batch, index, today, showClient = true, showMonth = true, showOwner = true }) {
  const f = batchFacts(batch, index, today);
  // A batch named just "Batch 01" (Start Month 1 now) does not repeat its number in the muted line.
  const number = batch.name?.trim() === batchLabel(batch.batchNumber) ? '' : batchLabel(batch.batchNumber);
  // Only a long client name truncates: "Batch 06 · Month 09" always stays visible (DESIGN §7.4).
  const clientName = showClient ? f.client?.name || '' : '';
  const rest = [number, showMonth && f.month && monthLabel(f.month.monthNumber)].filter(Boolean).join(' · ');
  const ownerName = f.owner?.name || 'Unassigned';
  return (
    <a class="table-row batch-row" href={batchHref(batch.id)}>
      <span class="br-batch">
        <span class="br-name truncate">{batch.name || batchLabel(batch.batchNumber)}</span>
        {/* "Batch 09" / "Month 10" never split across lines when the context wraps (768–999px) */}
        {(clientName || rest) && (
          <span class="br-context">
            {clientName && <span class="br-client truncate" title={clientName}>{clientName}</span>}
            {rest && <span class="br-rest">{clientName ? ' · ' : ''}{rest.replace(/(Batch|Month) (\d)/g, '$1\u00a0$2')}</span>}
          </span>
        )}
      </span>
      <span class="br-stage"><StageProgress batch={batch} showLabel /></span>
      {showOwner && (
        <span class="br-owner" title={ownerName}>
          <Avatar name={f.owner?.name} size={22} />
          <span class="truncate">{f.owner ? firstName(f.owner.name) : 'Unassigned'}</span>
        </span>
      )}
      <span class={cx('br-due num', f.overdue && 'is-overdue')} title={f.overdue ? 'Overdue' : undefined}>
        {f.due}
        {f.overdue && <span class="sr-only"> (overdue)</span>}
      </span>
      <span class="br-status"><StatusBadge status={batch.status} kind="batch" /></span>
    </a>
  );
}

// batches: already filtered + sorted. empty: node shown when there are none (default "No batches yet.").
// limit + onMore: render the first `limit` rows and a "Show more" row that calls onMore().
export function BatchTable({ batches = [], index, today, showClient = true, showMonth = true, showOwner = true, empty, limit, onMore, label = 'Batches', class: cls }) {
  if (!batches.length) return empty ?? <EmptyState title="No batches yet." compact />;
  const shown = limit ? batches.slice(0, limit) : batches;
  return (
    <div class={cx('table batch-table', !showOwner && 'batch-table-no-owner', cls)} role="group" aria-label={label}>
      <div class="table-row table-head batch-row" aria-hidden="true">
        <span>Batch</span>
        <span>Stage</span>
        {showOwner && <span>Owner</span>}
        <span>Due</span>
        <span>Status</span>
      </div>
      {shown.map((b) => <BatchRow key={b.id} batch={b} index={index} today={today} showClient={showClient} showMonth={showMonth} showOwner={showOwner} />)}
      {limit && batches.length > limit && (
        <button type="button" class="table-row batch-more" onClick={onMore}>
          Show more <span class="meta">({batches.length - limit} more)</span>
        </button>
      )}
    </div>
  );
}
